/**
 * VaultFormStorage - Servicio de almacenamiento de borradores de formularios
 *
 * Características:
 * - Persistencia en localStorage (sobrevive cierres de navegador)
 * - Vinculado a folderId (folio) + requirementId para evitar colisiones
 * - TTL configurable con limpieza automática de expirados
 * - Manejo de errores de quota
 */

const STORAGE_PREFIX = 'multiexpediente_vault_draft';
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

export interface StoredFormDraft<T = Record<string, unknown>> {
  data: T;
  folderId: string;
  requirementId: string;
  savedAt: string;
  expiry: number;
  version: string;
}

export interface AutoSaveMetadata {
  lastSaved: Date | null;
  hasStoredData: boolean;
}

/**
 * Genera la key de almacenamiento vinculada al folio y requirement
 */
function getStorageKey(folderId: string, requirementId: string): string {
  return `${STORAGE_PREFIX}_${folderId}_${requirementId}`;
}

/**
 * Guarda datos del formulario en localStorage con TTL
 */
export function saveFormDraft<T = Record<string, unknown>>(
  folderId: string,
  requirementId: string,
  data: T,
  ttlMs: number = DEFAULT_TTL_MS
): boolean {
  const key = getStorageKey(folderId, requirementId);

  const draft: StoredFormDraft<T> = {
    data,
    folderId,
    requirementId,
    savedAt: new Date().toISOString(),
    expiry: Date.now() + ttlMs,
    version: '1.0',
  };

  try {
    localStorage.setItem(key, JSON.stringify(draft));
    return true;
  } catch (error) {
    // Si hay error de quota, intentar limpiar expirados y reintentar
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      cleanupExpiredDrafts();
      try {
        localStorage.setItem(key, JSON.stringify(draft));
        return true;
      } catch {
        console.error('VaultFormStorage: Storage quota exceeded even after cleanup');
        return false;
      }
    }
    console.error('VaultFormStorage: Error saving draft:', error);
    return false;
  }
}

/**
 * Obtiene datos del formulario desde localStorage
 * Retorna null si no existe o está expirado
 */
export function getFormDraft<T = Record<string, unknown>>(
  folderId: string,
  requirementId: string
): StoredFormDraft<T> | null {
  const key = getStorageKey(folderId, requirementId);

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const draft: StoredFormDraft<T> = JSON.parse(stored);

    // Verificar expiración
    if (Date.now() > draft.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    // Verificar que pertenece al folder correcto (seguridad adicional)
    if (draft.folderId !== folderId || draft.requirementId !== requirementId) {
      localStorage.removeItem(key);
      return null;
    }

    return draft;
  } catch (error) {
    console.error('VaultFormStorage: Error getting draft:', error);
    // Limpiar entrada corrupta
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignorar si falla la limpieza
    }
    return null;
  }
}

/**
 * Elimina el borrador de un formulario específico
 */
export function clearFormDraft(folderId: string, requirementId: string): void {
  const key = getStorageKey(folderId, requirementId);
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('VaultFormStorage: Error clearing draft:', error);
  }
}

/**
 * Verifica si existe un borrador guardado (sin cargarlo completo)
 */
export function hasFormDraft(folderId: string, requirementId: string): boolean {
  const key = getStorageKey(folderId, requirementId);
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return false;

    const draft: StoredFormDraft = JSON.parse(stored);

    // Verificar expiración
    if (Date.now() > draft.expiry) {
      localStorage.removeItem(key);
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Obtiene metadata del borrador sin cargar todos los datos
 */
export function getFormDraftMetadata(
  folderId: string,
  requirementId: string
): AutoSaveMetadata {
  const key = getStorageKey(folderId, requirementId);

  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return { lastSaved: null, hasStoredData: false };
    }

    const draft: StoredFormDraft = JSON.parse(stored);

    if (Date.now() > draft.expiry) {
      localStorage.removeItem(key);
      return { lastSaved: null, hasStoredData: false };
    }

    return {
      lastSaved: new Date(draft.savedAt),
      hasStoredData: true,
    };
  } catch {
    return { lastSaved: null, hasStoredData: false };
  }
}

/**
 * Limpia todos los borradores expirados del vault
 */
export function cleanupExpiredDrafts(): number {
  let cleaned = 0;

  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
    const now = Date.now();

    keys.forEach(key => {
      try {
        const stored = localStorage.getItem(key);
        if (!stored) return;

        const draft: StoredFormDraft = JSON.parse(stored);
        if (draft.expiry < now) {
          localStorage.removeItem(key);
          cleaned++;
        }
      } catch {
        // Entrada corrupta, eliminar
        localStorage.removeItem(key);
        cleaned++;
      }
    });
  } catch (error) {
    console.error('VaultFormStorage: Error cleaning expired drafts:', error);
  }

  return cleaned;
}

/**
 * Limpia todos los borradores de un folder específico
 */
export function clearAllFolderDrafts(folderId: string): number {
  let cleaned = 0;
  const prefix = `${STORAGE_PREFIX}_${folderId}_`;

  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(prefix));
    keys.forEach(key => {
      localStorage.removeItem(key);
      cleaned++;
    });
  } catch (error) {
    console.error('VaultFormStorage: Error clearing folder drafts:', error);
  }

  return cleaned;
}

/**
 * Limpia todos los borradores del vault sin importar el folder
 */
export function clearAllVaultDrafts(): number {
  let cleaned = 0;

  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
    keys.forEach(key => {
      localStorage.removeItem(key);
      cleaned++;
    });
  } catch (error) {
    console.error('VaultFormStorage: Error clearing all drafts:', error);
  }

  return cleaned;
}

/**
 * Obtiene el tamaño aproximado de almacenamiento usado por los borradores
 */
export function getStorageUsage(): { count: number; sizeBytes: number } {
  let count = 0;
  let sizeBytes = 0;

  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX));
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        count++;
        sizeBytes += key.length + value.length;
      }
    });
  } catch {
    // Ignorar errores
  }

  return { count, sizeBytes };
}
